import * as angular from 'angular';
import * as _ from 'lodash';
import { keyBy } from 'lodash';

import ReportLinkService from '~/services/report-link-service';

import { tokenFromVizID } from '@mode/charts/domain';
import { Logger } from '@mode/shared/contract-common';
import { errorGif } from '@mode/shared/ui-images';
import { isBuilderMode, isDesignerMode, isLightboxMode, isReportMode } from '@mode/shared/util-js';
import { CHART_OPTION_TYPES, ChartOptionDefinition, DATA_LIMIT } from '../../../../common/constants';
import { WindowMessageService } from '../../../../services/window-message-service';

export const ChartController = function (
  $scope,
  reportLinkService: ReportLinkService,
  ChartSanitizer,
  ChartInterpolater,
  ChartThemeService,
  windowMessageService: WindowMessageService,
  $timeout,
  $log: Logger,
  $window
) {
  const ctrl = this;

  ctrl.$onInit = () => {
    $scope.onRenderStart({ $vizID: tokenFromVizID($scope.chartId) });
  };

  ctrl.trackRenderComplete = function trackRenderComplete() {
    $scope.$evalAsync(() => $scope.onRenderEnd({ $visualizationRendered: true }));
  };

  ctrl.isReportMode = () => {
    return isReportMode($window.document.body);
  };

  ctrl.isPreviewMode = () => {
    return isBuilderMode($window.document.body);
  };

  ctrl.isDesignerMode = () => {
    return isDesignerMode($window.document.body);
  };

  ctrl.titleEditDisabled = function titleEditDisabled() {
    return ctrl.isReportMode() && !ctrl.isPreviewMode();
  };

  ctrl.isLightboxMode = () => {
    return isLightboxMode($window.document.body);
  };

  $scope.linkable = reportLinkService.isLinkable();

  $scope.chartOptions = { types: keyBy(CHART_OPTION_TYPES.map(optionTypeToChartOption), 'type') };

  $scope.validTypes = [];

  $scope.isClipboardEnabled = false; // handled by clipboard directive

  $scope.simpleThemes = ['green', 'blue', 'orange', 'purple', 'gray'];

  if (ctrl.isLightboxMode()) {
    $scope.showSettings = true;
  }

  $scope.status = $scope.status || {
    unavailable: false,
    message: '',
  };

  $scope.errorImg = errorGif;

  $scope.title = {
    editing: false,
    value: '',
  };

  $scope.setTheme = function setTheme(selectedTheme) {
    $scope.view.theme = selectedTheme;
    $scope.view.chartColors = (
      ChartThemeService.getTheme(selectedTheme) || ChartThemeService.getTheme(ChartThemeService.DEFAULT_THEME_ID)
    ).colors;
  };

  function numberTypes(column) {
    return column.type === 'number' || column.type === 'integer' || column.type === 'real' || column.type === 'float';
  }

  function dataTypes(column) {
    return (
      column.type === 'number' ||
      column.type === 'integer' ||
      column.type === 'float' ||
      column.type === 'real' ||
      column.type === 'datetime' ||
      column.type === 'date'
    );
  }

  function dateTypes(column) {
    return column.type === 'datetime' || column.type === 'date';
  }

  function stringTypes(column) {
    return column.type === 'string';
  }

  $scope.isSelected = function (type) {
    if (angular.isUndefined($scope.view) || !$scope.view) {
      return false;
    }
    return type === $scope.view.selectedChart;
  };

  $scope.linkToData = function ($event) {
    if ($scope.linkable) {
      $event.preventDefault();
      $event.stopPropagation();
      reportLinkService.linkToPage($scope.data.query_token, 'data');
    }
  };

  $scope.linkToQuery = function ($event) {
    if ($scope.linkable) {
      $event.preventDefault();
      $event.stopPropagation();
      reportLinkService.linkToPage($scope.data.query_token, 'query');
    }
  };

  $scope.linkToLightbox = function ($event) {
    if ($scope.linkable) {
      $event.preventDefault();
      $event.stopPropagation();
      reportLinkService.linkToPage({ id: $scope.chartId, view: $scope.view }, 'lightbox');
    }
  };

  $scope.selectedType = function () {
    return $scope.view.selectedChart;
  };

  $scope.select = function (type) {
    if ($scope.view.selectedChart !== type && $scope.chartOptions.types[type]) {
      $scope.view.selectedChart = type;
    }
  };

  $scope.updateChartTitle = function (title) {
    $scope.view.chartTitle = title;
    updateChartSettings();
  };

  // Two mode of operation, if on the embedded report, onEdit will be undefined and it'll rely on
  // WindowMessageService to notify the main app to save, otherwise we'll assume the chart designer
  // can pass in an update function.
  function updateChartSettings() {
    if (typeof $scope.onEdit === 'function') {
      $scope.onEdit({ $id: $scope.chartId, $view: $scope.view });
    }
  }

  $scope.showChart = function () {
    return angular.isDefined($scope.view) && $scope.view != null && angular.isDefined($scope.data);
  };

  $scope.isDateFormat = function (format) {
    return (
      format === 'date' ||
      format === 'hour' ||
      format === 'full_mon' ||
      format === 'full_day' ||
      format === 'usa_full' ||
      format === 'mon_day' ||
      format === 'year' ||
      format === 'none'
    );
  };

  function updateChart(defaults) {
    if (!isValidChartData()) {
      $scope.status.unavailable = true;
      $scope.status.invalidData = true;
      $scope.status.message = $scope.view.errorMsg || '';
      $scope.onRenderEnd({ $visualizationRendered: false });
      return;
    }

    if (isDatasetLarge($scope.data) && !defaults.aggregate) {
      $scope.status.unavailable = true;
      $scope.status.message = 'Mode charts will display up to 20K data points right now';
      return;
    }

    let data = _.cloneDeep($scope.data.content) || [];

    let opts = {};
    if (defaults && defaults.type) {
      const xtype = <any>_.find($scope.data.columns, function (column: any) {
        return column.name === defaults.x;
      });
      if (xtype && (xtype.type === 'datetime' || xtype.type === 'date')) {
        data = _.sortBy(data, function (row) {
          return new Date(row[$scope.view[$scope.selectedType()].x]);
        });
        opts = { xformat: $scope.isDateFormat(defaults.xformat) ? defaults.xformat : 'date' };
      } else if (xtype && xtype.type !== 'datetime' && xtype.type !== 'date' && $scope.isDateFormat(defaults.xformat)) {
        delete defaults.xformat;
      }
      if (xtype && !dataTypes(xtype)) {
        opts = { xtype: 'string' };
      }

      if (defaults.g && defaults.f && _.includes(defaults.f, '$mode-select-all$')) {
        defaults.f = _.chain(data)
          .map(defaults.g)
          .uniq()
          .map(function (seriesName) {
            return seriesName;
          })
          .value()
          .sort(function (a, b) {
            return a > b ? 1 : a < b ? -1 : 0;
          });
      }
    }

    $scope.status.unavailable = false;
    $scope.status.message = '';
    $scope.render(data, $scope.selectedType(), angular.extend({}, defaults, opts));
  }

  const isDataContained = function (dataset) {
    return dataset != null && angular.isDefined(dataset.columns) && angular.isDefined(dataset.content);
  };

  function isDatasetLarge(dataset) {
    return angular.isDefined(dataset.count) && dataset.count > DATA_LIMIT;
  }

  function hasChartDefaults(chart, defaults) {
    switch (chart) {
      case 'pie':
        return angular.isDefined(defaults.value) && angular.isDefined(defaults.label);
      case 'bar':
      case 'line':
      case 'linePlusBar':
      case 'area':
        return (
          angular.isDefined(defaults.x) && (angular.isDefined(defaults.y) || angular.isDefined(defaults.aggregate))
        );
      default:
        return angular.isDefined(defaults.x) && angular.isDefined(defaults.y);
    }
  }

  function isValidChartData() {
    if (!$scope.view.selectedChart) {
      $log.warn('No chart type selected');
      return false;
    }
    const chart = $scope.view.selectedChart;

    if (angular.isUndefined($scope.view[chart]) || !hasChartDefaults(chart, $scope.view[chart])) {
      $log.warn('No settings found for chart');
      return false;
    }

    if (
      (chart === 'line' || chart === 'area' || chart === 'scatter') &&
      angular.isUndefined($scope.view[chart].label)
    ) {
      $log.warn('Chart has no label');
      return false;
    }

    return true;
  }
  let initData;
  function refreshData(dataset) {
    if ($scope.view) {
      $scope.data = dataset && dataset.datasets ? dataset.datasets[$scope.view._dataset] : dataset;
      if (isDataContained($scope.data) && !isDatasetLarge($scope.data)) {
        initData = true;

        ChartSanitizer.sanitize($scope.view, $scope.data, !ctrl.isLightboxMode());
        let tempIndex;
        if (ctrl.isLightboxMode()) {
          $scope.validTypes = ChartInterpolater.interpolate($scope.view, $scope.data, $scope.chartOptions);

          if (
            (!$scope.view.selectedChart && $scope.validTypes.length) ||
            ($scope.validTypes.length && !_.find($scope.validTypes, { type: $scope.view.selectedChart }))
          ) {
            $scope.select($scope.validTypes[0].type);
          } else if ($scope.view.selectedChart && _.find($scope.validTypes, { type: $scope.view.selectedChart })) {
            tempIndex = $scope.view.selectedChart;
            $scope.view.selectedChart = undefined;
            $scope.select(tempIndex);
          } else {
            $log.warn('Selected chart type is not valid with given data');
            $scope.status.unavailable = true;
            $scope.status.invalidData = true;
            $scope.status.message = '';
          }
        } else {
          if (!isValidChartData()) {
            $scope.status.unavailable = true;
            $scope.status.invalidData = true;
            $scope.status.message = '';
          } else {
            // Previous hack no longer works in embed v2
            // force redraw on data change in the v2 world instead
            if ($scope.redrawOnDataChange === 'true') {
              updateChart(angular.extend({ type: $scope.view.selectedChart }, $scope.view[$scope.view.selectedChart]));
            } else {
              tempIndex = $scope.view.selectedChart;
              $scope.view.selectedChart = undefined;
              $scope.select(tempIndex);
            }
          }
        }
      } else if (isDataContained($scope.data) && isDatasetLarge($scope.data)) {
        $scope.status.unavailable = true;
        $scope.status.message = 'Mode charts will display up to 20K data points right now';
      }
    }
  }

  /**********************************************************************************
   * watchers for model changes
   */
  $scope.$watch('dataset', refreshData);
  const dataset = $scope.dataset;
  $scope.data = dataset && dataset.datasets ? dataset.datasets[$scope.view._dataset] : dataset;

  const isDataSafe = function (defaults) {
    return (
      angular.isDefined(defaults) &&
      angular.isDefined($scope.view) &&
      angular.isDefined($scope.view.selectedChart) &&
      angular.isDefined($scope.data)
    );
  };
  const isAddRemoveLabel = function (defaults, old) {
    return (
      (angular.isDefined(defaults.ylabel) && defaults.ylabel !== old.ylabel && defaults.ylabel.length > 1) ||
      (angular.isDefined(defaults.xlabel) && defaults.xlabel !== old.xlabel && defaults.xlabel.length > 1)
    );
  };

  function isChartTitle(newView, oldView) {
    return angular.isDefined(newView.chartTitle) && newView.chartTitle !== oldView.chartTitle;
  }

  $scope.$watch(
    '[chartId,view]',
    function (newValues: Array<any>, oldValues: Array<any>) {
      const oldId = oldValues[0];
      const newId = newValues[0];

      const newView = newValues[1];
      const oldView = oldValues[1];

      if (newView && oldView && newView.selectedChart && isDataSafe(newView[newView.selectedChart])) {
        const defaults = newView[newView.selectedChart];
        const old = oldView[newView.selectedChart];
        if (
          (!isAddRemoveLabel(defaults, old) && !isChartTitle(newView, oldView)) ||
          oldId !== newId ||
          oldView.selectedChart !== newView.selectedChart
        ) {
          updateChart(angular.extend({ type: newView.selectedChart }, defaults));
        }
      }
    },
    true
  );
};

ChartController.$inject = [
  '$scope',
  'ReportLinkService',
  'ChartSanitizerService',
  'ChartInterpolaterService',
  'ChartThemeService',
  'WindowMessageService',
  '$timeout',
  '$log',
  '$window',
];

function optionTypeToChartOption(option: ChartOptionDefinition) {
  return {
    name: option.name,
    type: option.id,
    icon: option.iconTrunc,
  };
}
